import React, { useEffect, useState } from "react";
import { Redirect, withRouter } from "react-router-dom";
import Footer from "../components/Footer.jsx";
import Spinner from "../profile/Information/Spinner.jsx";
import SmartIdService from "components/services/SmartidService.js";
import { SmartIdContext } from "components/tools/context/SmartIdContext.jsx";
import { withTranslation } from "react-i18next";
import { LoginPageTitle } from './Login/LoginPageTitle.jsx';
import { LoginHeader } from './Login/LoginHeader.jsx';
import { LoginFooter } from './Login/LoginFooter.jsx';
import { PasswordLoginForm } from './Login/password/PasswordLoginForm.jsx';
import { LoginEmailVerificationFlow } from './Login/LoginEmailVerificationFlow.jsx';
import { usePasswordLoginLogic } from './Login/usePasswordLoginLogic.js';
import { useLoginEmailVerificationFlowLogic } from './Login/useLoginEmailVerificationFlowLogic.js';
import { SmartIdLoginWizard } from "./Login/smartid/SmartIdLoginWizard.jsx";
import * as constants from "components/constants/constants.jsx";
import LegalEntityFunctions from "components/components/LegalEntityFunctions.jsx";
import { MethodSelector } from "components/SmartId/Shared/MethodSelector.jsx";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";

function Login(props) {
  const [recaptchaValidationTrigger, setRecaptchaValidationTrigger] = useState(undefined);

  const {
    state,
    updateState,
    onChange,
    switchLogin,
    login,
    executeUnauthenticatedRedirections,
    setInitialState,
    onDropDownChange,
    logout,
    resetInitialState,
    setIsProcessing
  } = usePasswordLoginLogic(props.initialData, props.location, props.history);
  
  const { onAddEmail, onBirthDateChange, addEmail } = useLoginEmailVerificationFlowLogic(state, updateState);

	const shouldSmartIdMethodBeSelected = (countryCode) => {
    const result = constants.COUNTRIES_WITH_SMART_ID_AS_DEFAULT.includes(countryCode);
		return result;
	};

  const [isSmartIdMethodSelected, setIsSmartIdMethodSelected] = useState(false);
  const [isSmartIdEnabled, setIsSmartIdEnabled] = useState(SmartIdService.isSmartIdEnabled(props.initialData.smartIdConfig));
  const [selectedCountryLegalEntityId, setSelectedCountryLegalEntityId] = useState(LegalEntityFunctions.getLegalEntityId(localStorage.getItem(constants.SELECTED_COUNTRY)));

  useEffect(() => {
    executeUnauthenticatedRedirections();
    setInitialState();
    
    const handleCountryChange = (ev) => countryChanged(ev.detail);
    window.addEventListener(constants.COUNTRY_CHANGED, handleCountryChange);
    console.log("Login component mounted with initialData:", props.initialData);
    handleSmartIdToggleEdgeCase();

    return () => {
      window.removeEventListener(constants.COUNTRY_CHANGED, handleCountryChange);
    };
  }, [executeUnauthenticatedRedirections, setInitialState]);

  // Reset reCAPTCHA trigger when login errors occur
  useEffect(() => {
    if (state.errors && state.errors.length > 0) {
      setRecaptchaValidationTrigger(undefined);
    }
  }, [state.errors]);

  const handleSubmit = (e) => {
    e.preventDefault();
  };

	const countryChanged = (countryCode) => {
    const isSmartIdEnabled = SmartIdService.isSmartIdEnabled(props.initialData.smartIdConfig);
		setIsSmartIdEnabled(isSmartIdEnabled);
		if (isSmartIdEnabled) {
      console.debug("[Login] Smart-ID enabled for country, evaluating default selection");
      setIsSmartIdMethodSelected(shouldSmartIdMethodBeSelected(countryCode));
    }
    setSelectedCountryLegalEntityId(LegalEntityFunctions.getLegalEntityId(countryCode));
    handleSmartIdToggleEdgeCase();
	};	

  const handleSmartIdToggleEdgeCase = () => {
    if (!isSmartIdEnabled) {
      setIsSmartIdMethodSelected(false);
    }
  };

  const handleMethodChange = (isSmartIdSelected) => {
    setIsSmartIdMethodSelected(isSmartIdSelected);
    console.debug(`Authentication method changed to ${isSmartIdSelected ? 'Smart-ID' : 'Email'}`);
  };

	const beginRecaptchaValidation = () => {
		setIsProcessing(true);
		setRecaptchaValidationTrigger(!recaptchaValidationTrigger);
	};

	const completeRecaptchaValidation = (token) => {
		// Reset the trigger immediately to prevent re-rendering
		setRecaptchaValidationTrigger(undefined);
		login(token);
	};

  const renderLoginForm = () => (
    <div className="container">
      <div className="card">
        <LoginHeader
          initialData={props.initialData}
          isMobileApp={state.isMobileApp}
          isLoginTypeSwitchHidden={state.isLoginTypeSwitchHidden}
          businessLogin={state.businessLogin}
          onSwitchLogin={switchLogin}
        />
        <div className="card-content">          
          <LoginPageTitle businessLogin={state.businessLogin} />
          {!state.businessLogin && (
            <MethodSelector
              isSmartIdEnabled={isSmartIdEnabled}
              isSmartIdMethodSelected={isSmartIdMethodSelected}						
              onMethodChange={handleMethodChange}
              textKey="login_page_choose_authentication_method"
            />
          )}
          {(isSmartIdMethodSelected && !state.businessLogin) ? (
            <SmartIdLoginWizard
              legalEntityId={selectedCountryLegalEntityId}
            />
          ) : (
            <>
            <PasswordLoginForm
              state={state}
              onSubmit={handleSubmit}
              onChange={onChange}
              onBeginRecaptchaValidation={beginRecaptchaValidation}
              onResetInitialState={resetInitialState}
              onDropDownChange={onDropDownChange}
              initialData={props.initialData}
            />
            {recaptchaValidationTrigger !== undefined && (
              <GoogleReCaptcha 
                onVerify={completeRecaptchaValidation} 
                refreshReCaptcha={beginRecaptchaValidation}
                aria-label="Complete reCAPTCHA verification to proceed"
              />
            )}
            </>
          )}
        </div>
        <LoginFooter
          businessLogin={state.businessLogin}
          phoneError={state.phoneError}         
        />
      </div>
    </div>
  );

  // Early returns for different states
  if (state.showSpinner) {
    return <Spinner />;
  }

  if (state.selectedCountry === null) {
    return <Redirect to={"/CountrySelection" + props.location.search} />;
  }

  return (
    <>
      <SmartIdContext.Provider value={isSmartIdEnabled}>
        <div>
          {(state.showEmailSent || state.showEnterEmail) ? (
            <LoginEmailVerificationFlow
              state={state}
              initialData={props.initialData}
              onAddEmail={onAddEmail}
              onBirthDateChange={onBirthDateChange}
              onAddEmailSubmit={addEmail}
              onLogout={logout}
              location={props.location}
              businessLogin={state.businessLogin}
            />
          ) : (
            renderLoginForm()
          )}
        </div>
      </SmartIdContext.Provider>
      <Footer countrySelectionEnabled={true} />
    </>
  );
}

export default withRouter(withTranslation()(Login));
